import {
    DEFAULT_DROUGHT_FILTER,
    DROUGHT_FILTER_ITEMS,
} from '../../pages/components/drought-warning/droughtWarning.constants';

const withOpacity = (opacity, alpha) => Number((opacity * alpha).toFixed(3));
const rgba = (color, opacity, alpha = 1) => [...color, withOpacity(opacity, alpha)];
const toCssColor = (color) => `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

const FALLBACK_STYLE = {
    fill: [255, 255, 255],
    stroke: [203, 213, 225],
    fillAlpha: 0.2,
    strokeAlpha: 1,
    strokeWidth: 1,
};

const resolveRule = (value, rules) => {
    if (value === null || typeof value === 'undefined') {
        return FALLBACK_STYLE;
    }

    return rules.find((rule) => rule.test(value)) || FALLBACK_STYLE;
};

export const FILTER_STYLE_MAP = {
    drought: {
        legendName: 'Aszály',
        selector: (grid) => grid.is_drought,
        rules: [
            {
                label: 'Aszályos',
                test: (value) => value === true,
                fill: [185, 28, 28],
                stroke: [153, 27, 27],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.6,
            },
            {
                label: 'Nem aszályos',
                test: (value) => value === false,
                fill: [22, 163, 74],
                stroke: [21, 128, 61],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.2,
            },
        ],
    },
    'daily-t': {
        legendName: 'Napi T max',
        selector: (grid) => grid.daily_tmax_c,
        rules: [
            {
                label: '>= 31 C',
                test: (value) => value >= 31,
                fill: [185, 28, 28],
                stroke: [153, 27, 27],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.7,
            },
            {
                label: '28-30.9 C',
                test: (value) => value >= 28,
                fill: [234, 88, 12],
                stroke: [194, 65, 12],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.5,
            },
            {
                label: '24-27.9 C',
                test: (value) => value >= 24,
                fill: [245, 158, 11],
                stroke: [217, 119, 6],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.3,
            },
            {
                label: '20-23.9 C',
                test: (value) => value >= 20,
                fill: [132, 204, 22],
                stroke: [77, 124, 15],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.1,
            },
            {
                label: '< 20 C',
                test: () => true,
                fill: [59, 130, 246],
                stroke: [29, 78, 216],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1,
            },
        ],
    },
    'daily-mm': {
        legendName: 'Napi csapadek',
        selector: (grid) => grid.daily_precip_mm,
        rules: [
            {
                label: '0 mm',
                test: (value) => value <= 0,
                fill: [185, 28, 28],
                stroke: [153, 27, 27],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.7,
            },
            {
                label: '0.1-2 mm',
                test: (value) => value <= 2,
                fill: [249, 115, 22],
                stroke: [194, 65, 12],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.5,
            },
            {
                label: '2.1-5 mm',
                test: (value) => value <= 5,
                fill: [245, 158, 11],
                stroke: [217, 119, 6],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.3,
            },
            {
                label: '5.1-10 mm',
                test: (value) => value <= 10,
                fill: [56, 189, 248],
                stroke: [2, 132, 199],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.1,
            },
            {
                label: '> 10 mm',
                test: () => true,
                fill: [37, 99, 235],
                stroke: [29, 78, 216],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1,
            },
        ],
    },
    'precip-30d': {
        legendName: '30 napos csapadek',
        selector: (grid) => grid.precip_30d_sum_mm,
        rules: [
            {
                label: '< 20 mm',
                test: (value) => value < 20,
                fill: [185, 28, 28],
                stroke: [153, 27, 27],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.7,
            },
            {
                label: '20-39.9 mm',
                test: (value) => value < 40,
                fill: [234, 88, 12],
                stroke: [194, 65, 12],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.5,
            },
            {
                label: '40-69.9 mm',
                test: (value) => value < 70,
                fill: [245, 158, 11],
                stroke: [217, 119, 6],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.3,
            },
            {
                label: '70-99.9 mm',
                test: (value) => value < 100,
                fill: [74, 222, 128],
                stroke: [22, 163, 74],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.1,
            },
            {
                label: '>= 100 mm',
                test: () => true,
                fill: [37, 99, 235],
                stroke: [29, 78, 216],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1,
            },
        ],
    },
    'hot-days-gt-31': {
        legendName: '31 C feletti napok',
        selector: (grid) => grid.hot_days_gt_31_count,
        rules: [
            {
                label: '>= 10 nap',
                test: (value) => value >= 10,
                fill: [185, 28, 28],
                stroke: [153, 27, 27],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.7,
            },
            {
                label: '6-9 nap',
                test: (value) => value >= 6,
                fill: [234, 88, 12],
                stroke: [194, 65, 12],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.5,
            },
            {
                label: '3-5 nap',
                test: (value) => value >= 3,
                fill: [245, 158, 11],
                stroke: [217, 119, 6],
                fillAlpha: 0.56,
                strokeAlpha: 1,
                strokeWidth: 1.3,
            },
            {
                label: '1-2 nap',
                test: (value) => value >= 1,
                fill: [132, 204, 22],
                stroke: [77, 124, 15],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1.1,
            },
            {
                label: '0 nap',
                test: () => true,
                fill: [22, 163, 74],
                stroke: [21, 128, 61],
                fillAlpha: 1,
                strokeAlpha: 1,
                strokeWidth: 1,
            },
        ],
    },
};

export const getNormalizedDroughtFilter = (filter) => {
    if (DROUGHT_FILTER_ITEMS.some((item) => item.value === filter)) {
        return filter;
    }

    return DEFAULT_DROUGHT_FILTER;
};

export const getDroughtGridStyle = (grid, filter, opacity) => {
    const normalizedFilter = getNormalizedDroughtFilter(filter);
    const styleDefinition = FILTER_STYLE_MAP[normalizedFilter];
    const rule = resolveRule(styleDefinition.selector(grid), styleDefinition.rules);
    const { isSelected } = grid;

    return {
        fill: rgba(rule.fill, opacity, rule.fillAlpha),
        stroke: isSelected ? [15, 23, 42, 1] : rgba(rule.stroke, opacity, rule.strokeAlpha),
        strokeWidth: isSelected ? Math.max(rule.strokeWidth + 2, 3.5) : rule.strokeWidth,
        lineDash: isSelected ? [10, 6] : undefined,
        zIndex: isSelected ? 10 : 0,
    };
};

export const getDroughtLegendDefinition = (filter) => {
    const normalizedFilter = getNormalizedDroughtFilter(filter);
    const styleDefinition = FILTER_STYLE_MAP[normalizedFilter];

    return {
        name: styleDefinition.legendName,
        scale: styleDefinition.rules.map((rule) => ({
            fill: toCssColor(rule.fill),
            stroke: toCssColor(rule.stroke),
            strokeWidth: rule.strokeWidth,
        })),
        minMax: styleDefinition.rules.map((rule) => rule.label),
    };
};
